import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { referencePricingAPI } from '../services/api';
import {
  DollarSign, Package, BarChart3, Search, RefreshCw,
  ChevronDown, ChevronRight, ExternalLink, TrendingUp,
  FileText, Award, Target, Box, ShoppingCart, Link2
} from 'lucide-react';
import './Analytics.css';

const QUALITY_LABELS = { high: 'High', medium: 'Medium', low: 'Low' };
const QUALITY_CLASSES = { high: 'quality-high', medium: 'quality-medium', low: 'quality-low' };

const SOURCE_DISPLAY_ORDER = ['gsa_schedule', 'grainger', 'mcmaster_carr', 'uline', 'amazon', 'alibaba'];
const SOURCE_LABELS = {
  gsa_schedule: 'GSA',
  grainger: 'Grainger',
  mcmaster_carr: 'McMaster',
  uline: 'Uline',
  amazon: 'Amazon',
  alibaba: 'Alibaba',
};
const SOURCE_COMING_SOON = new Set(['amazon', 'alibaba']);

const POPULAR_SEARCHES = [
  'Paper Products',
  'Safety Equipment',
  'IT Hardware',
  'Cleaning Supplies',
  'Office Furniture',
  'Medical Supplies',
];

const getSourcePrices = (product) => {
  const map = {};
  for (const p of (product.prices || [])) {
    const src = (p.source || '').toLowerCase().replace(/[\s-]+/g, '_');
    const cad = p.unit_price_cad || p.unit_price || 0;
    if (!map[src] || cad < map[src].cad) {
      map[src] = { cad, currency: p.currency || 'CAD', original: p.unit_price || 0, vendor: p.vendor };
    }
  }
  return map;
};

const formatPrice = (val) => {
  if (val == null || val === 0) return '—';
  return `$${val.toFixed(2)}`;
};

const getDelta = (price, best) => {
  if (!price || !best || best === 0) return null;
  const pct = ((price - best) / best) * 100;
  if (pct <= 0.5) return null;
  return `+${pct.toFixed(0)}%`;
};

// =============================================================================
// Collapsible Section
// =============================================================================
const CollapsibleSection = ({ icon: Icon, title, badge, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`collapsible-section ${open ? 'open' : ''}`}>
      <button className="collapsible-toggle" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Icon size={14} />
        <span>{title}</span>
        {badge != null && <span className="collapsible-badge">{badge}</span>}
      </button>
      {open && <div className="collapsible-body">{children}</div>}
    </div>
  );
};

// =============================================================================
// Historical Awards Section (lazy-loaded)
// =============================================================================
const HistoricalAwards = ({ product }) => {
  const [contracts, setContracts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const keywords = product.product_name || '';
    const category = product.category || '';
    referencePricingAPI.getContracts(keywords, category, 5)
      .then(data => setContracts(data.contracts || []))
      .catch(() => setContracts([]))
      .finally(() => setLoading(false));
  }, [product]);

  if (loading) return <div className="section-loading"><RefreshCw size={14} className="spinning" /> Loading...</div>;
  if (!contracts || contracts.length === 0) return <div className="section-empty">No historical awards found.</div>;

  return (
    <div className="contracts-table-wrapper">
      <table className="contracts-table">
        <thead>
          <tr>
            <th>Supplier</th>
            <th>Value</th>
            <th>Date</th>
            <th>Method</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((c, i) => (
            <tr key={i}>
              <td className="contract-supplier">{c.supplier_legal_name || '—'}</td>
              <td className="contract-value">
                {c.contract_amount ? `$${Number(c.contract_amount).toLocaleString()}` : '—'}
              </td>
              <td className="contract-date">
                {c.contract_award_date
                  ? new Date(c.contract_award_date).toLocaleDateString('en-CA', { year: 'numeric', month: 'short' })
                  : '—'}
              </td>
              <td>
                <span className={`method-badge ${
                  (c.procurement_method || '').toLowerCase().includes('competitive') ? 'method-competitive' :
                  (c.procurement_method || '').toLowerCase().includes('sole') ? 'method-sole' : ''
                }`}>
                  {c.procurement_method || '—'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// =============================================================================
// Win Intelligence Section (lazy-loaded)
// =============================================================================
const WinIntelligence = ({ product }) => {
  const [benchmarks, setBenchmarks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const category = product.category || '';
    referencePricingAPI.getBenchmarks(product.gsin_code || '', category)
      .then(data => setBenchmarks(data.benchmarks || []))
      .catch(() => setBenchmarks([]))
      .finally(() => setLoading(false));
  }, [product]);

  if (loading) return <div className="section-loading"><RefreshCw size={14} className="spinning" /> Loading...</div>;

  const benchmark = benchmarks && benchmarks.length > 0 ? benchmarks[0] : null;
  if (!benchmark) return <div className="section-empty">No benchmark data available.</div>;

  return (
    <div className="win-intelligence">
      <div className="win-section">
        <h5>How Bids Are Won</h5>
        <div className="win-bars">
          <div className="win-bar-row">
            <span className="win-bar-label">Lowest Price</span>
            <div className="win-bar-track">
              <div
                className="win-bar-fill win-bar-price"
                style={{ width: `${Math.min((benchmark.lowest_price_win_rate || 0) * 100, 100)}%` }}
              />
            </div>
            <span className="win-bar-value">{((benchmark.lowest_price_win_rate || 0) * 100).toFixed(0)}%</span>
          </div>
          <div className="win-bar-row">
            <span className="win-bar-label">Best Value</span>
            <div className="win-bar-track">
              <div
                className="win-bar-fill win-bar-value-fill"
                style={{ width: `${Math.min((1 - (benchmark.lowest_price_win_rate || 0)) * 100, 100)}%` }}
              />
            </div>
            <span className="win-bar-value">{((1 - (benchmark.lowest_price_win_rate || 0)) * 100).toFixed(0)}%</span>
          </div>
        </div>
      </div>

      {benchmark.top_suppliers && benchmark.top_suppliers.length > 0 && (
        <div className="win-section">
          <h5>Top Suppliers</h5>
          <table className="top-suppliers-table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Wins</th>
                <th>Rate</th>
                <th>Avg Value</th>
              </tr>
            </thead>
            <tbody>
              {benchmark.top_suppliers.slice(0, 5).map((s, i) => (
                <tr key={i}>
                  <td>{s.name || s.supplier_name || '—'}</td>
                  <td>{s.win_count || 0}</td>
                  <td>{((s.win_rate || 0) * 100).toFixed(0)}%</td>
                  <td>{s.avg_contract_value ? `$${Number(s.avg_contract_value).toLocaleString()}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {benchmark.percentiles && (
        <div className="win-section">
          <h5>Price Positioning</h5>
          <div className="percentile-bar">
            <div className="percentile-zone percentile-low" style={{ width: '25%' }}>
              <span>P10: ${Number(benchmark.percentiles.p10 || 0).toLocaleString()}</span>
            </div>
            <div className="percentile-zone percentile-mid" style={{ width: '50%' }}>
              <span>Median: ${Number(benchmark.median_contract_value || 0).toLocaleString()}</span>
            </div>
            <div className="percentile-zone percentile-high" style={{ width: '25%' }}>
              <span>P90: ${Number(benchmark.percentiles.p90 || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Linked Tenders Section (lazy-loaded)
// =============================================================================
const LinkedTenders = ({ productId }) => {
  const [tenders, setTenders] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    referencePricingAPI.getLinkedTenders(productId)
      .then(data => setTenders(data.tenders || []))
      .catch(() => setTenders([]))
      .finally(() => setLoading(false));
  }, [productId]);

  if (loading) return <div className="section-loading"><RefreshCw size={14} className="spinning" /> Loading...</div>;
  if (!tenders || tenders.length === 0) return <div className="section-empty">No active tenders found.</div>;

  return (
    <div className="linked-tender-cards">
      {tenders.map((t, i) => (
        <Link key={i} to={`/tender/${encodeURIComponent(t.tender_id)}`} className="linked-tender-card">
          <ExternalLink size={14} />
          <span className="linked-tender-title">{t.title}</span>
          {t.closing_date && (
            <span className="linked-tender-date">
              Closes {new Date(t.closing_date).toLocaleDateString()}
            </span>
          )}
          {t.organization && (
            <span className="linked-tender-org">{t.organization}</span>
          )}
        </Link>
      ))}
    </div>
  );
};

// =============================================================================
// Product Card (expandable)
// =============================================================================
const ProductCard = ({ product }) => {
  const [expanded, setExpanded] = useState(false);
  const sourcePrices = useMemo(() => getSourcePrices(product), [product]);
  const bestPrice = product.best_price_cad || 0;

  const lastUpdated = product.last_updated
    ? new Date(product.last_updated).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
    : null;

  // Count sources with actual prices
  const sourceCount = Object.keys(sourcePrices).length;
  const tenderCount = product.linked_tender_count || null;

  return (
    <div className={`product-card ${expanded ? 'product-card-expanded' : ''}`}>
      {/* Collapsed Header — always visible, clickable */}
      <div className="product-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="product-card-header-left">
          <span className="product-card-chevron">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <div className="product-card-header-info">
            <h3 className="product-card-title">{product.product_name}</h3>
            <div className="product-card-meta">
              {product.category && <span className="meta-tag">{product.category}</span>}
              {product.data_quality && (
                <span className={`quality-badge ${QUALITY_CLASSES[product.data_quality] || ''}`}>
                  {QUALITY_LABELS[product.data_quality] || product.data_quality}
                </span>
              )}
              {lastUpdated && <span className="meta-date">Updated {lastUpdated}</span>}
            </div>
          </div>
        </div>
        <div className="product-card-header-right">
          {bestPrice > 0 && (
            <span className="header-best-price">{formatPrice(bestPrice)}</span>
          )}
          {sourceCount > 0 && (
            <span className="header-source-count">{sourceCount} source{sourceCount !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      {/* Expanded Body */}
      {expanded && (
        <>
          {/* 3-Column Layout */}
          <div className="product-card-columns">
            {/* Column 1: Pricing */}
            <div className="card-column pricing-column">
              <div className="column-header">
                <DollarSign size={14} />
                <span>Pricing</span>
              </div>
              <div className="pricing-source-list">
                {SOURCE_DISPLAY_ORDER.map(srcKey => {
                  const isComingSoon = SOURCE_COMING_SOON.has(srcKey);
                  const priceData = sourcePrices[srcKey];
                  const isBest = priceData && priceData.cad === bestPrice && bestPrice > 0;
                  const delta = priceData ? getDelta(priceData.cad, bestPrice) : null;

                  return (
                    <div key={srcKey} className={`price-row ${isBest ? 'price-best' : ''}`}>
                      <span className={`source-badge badge-${srcKey}`}>
                        {SOURCE_LABELS[srcKey]}
                      </span>
                      {isComingSoon ? (
                        <span className="price-coming-soon">Coming Soon</span>
                      ) : priceData ? (
                        <span className="price-value">
                          <span className="price-amount">{formatPrice(priceData.cad)}</span>
                          {isBest && <span className="best-indicator">BEST</span>}
                          {delta && <span className="price-delta">{delta}</span>}
                        </span>
                      ) : (
                        <span className="price-none">—</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {bestPrice > 0 && (
                <div className="best-price-summary">
                  Best: <strong>{formatPrice(bestPrice)}</strong>
                </div>
              )}
            </div>

            {/* Column 2: Stock & Inventory (Placeholder) */}
            <div className="card-column placeholder-column">
              <div className="column-header">
                <Box size={14} />
                <span>Stock & Inventory</span>
              </div>
              <div className="placeholder-content">
                <Package size={32} className="placeholder-icon" />
                <p className="placeholder-text">Connect your inventory system to track stock levels</p>
                <button className="placeholder-btn" disabled>
                  Coming Soon
                </button>
              </div>
            </div>

            {/* Column 3: Supply Arrangements (Placeholder) */}
            <div className="card-column placeholder-column">
              <div className="column-header">
                <ShoppingCart size={14} />
                <span>Supply Arrangements</span>
              </div>
              <div className="placeholder-content">
                <Link2 size={32} className="placeholder-icon" />
                <p className="placeholder-text">No supply arrangements configured</p>
                <button className="placeholder-btn" disabled>
                  + Add Arrangement — Coming Soon
                </button>
              </div>
            </div>
          </div>

          {/* Collapsible Sections */}
          <div className="product-card-sections">
            <CollapsibleSection icon={FileText} title="View Historical Awards">
              <HistoricalAwards product={product} />
            </CollapsibleSection>
            <CollapsibleSection icon={Award} title="View Win Intel">
              <WinIntelligence product={product} />
            </CollapsibleSection>
            <CollapsibleSection
              icon={Target}
              title="Linked Tenders"
              badge={tenderCount}
            >
              <LinkedTenders productId={product.id} />
            </CollapsibleSection>
          </div>
        </>
      )}
    </div>
  );
};

// =============================================================================
// Category Summary Panel
// =============================================================================
const CategorySummary = ({ products, benchmarks, searchQuery }) => {
  const summary = useMemo(() => {
    if (!products || products.length === 0) return null;

    const prices = products
      .map(p => p.best_price_cad)
      .filter(p => p && p > 0);

    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
    const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
    const avgPrice = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;

    const totalSources = products.reduce((sum, p) => sum + (p.price_count || (p.prices || []).length || 0), 0);
    const avgSources = products.length > 0 ? (totalSources / products.length).toFixed(1) : '0';

    return { count: products.length, minPrice, maxPrice, avgPrice, avgSources };
  }, [products]);

  if (!summary) return null;

  const benchmark = benchmarks && benchmarks.length > 0 ? benchmarks[0] : null;

  return (
    <div className="category-summary">
      <div className="category-summary-header">
        <h2>"{searchQuery}"</h2>
        <span className="category-summary-count">{summary.count} products found</span>
      </div>

      <div className="category-metrics">
        <div className="cat-metric">
          <span className="cat-metric-label">Products</span>
          <span className="cat-metric-value">{summary.count}</span>
        </div>
        <div className="cat-metric">
          <span className="cat-metric-label">Price Range</span>
          <span className="cat-metric-value">{formatPrice(summary.minPrice)}–{formatPrice(summary.maxPrice)}</span>
        </div>
        <div className="cat-metric">
          <span className="cat-metric-label">Avg Price</span>
          <span className="cat-metric-value">{formatPrice(summary.avgPrice)}</span>
        </div>
        <div className="cat-metric">
          <span className="cat-metric-label">Sources</span>
          <span className="cat-metric-value">{summary.avgSources} avg/prod</span>
        </div>
      </div>

      {benchmark && (
        <div className="category-summary-footer">
          {benchmark.top_suppliers && benchmark.top_suppliers.length > 0 && (
            <span className="summary-insight">
              Top Supplier: {benchmark.top_suppliers[0].name || benchmark.top_suppliers[0].supplier_name}
              {benchmark.top_suppliers[0].win_rate
                ? ` (${(benchmark.top_suppliers[0].win_rate * 100).toFixed(0)}% win)`
                : ''}
            </span>
          )}
          {benchmark.lowest_price_win_rate != null && (
            <span className="summary-insight">
              Selection: {((benchmark.lowest_price_win_rate || 0) * 100).toFixed(0)}% Lowest Price / {((1 - (benchmark.lowest_price_win_rate || 0)) * 100).toFixed(0)}% Best Value
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Main Analytics Page
// =============================================================================
const Analytics = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-persisted state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [quality, setQuality] = useState(searchParams.get('quality') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'best_price_cad:asc');
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const pageSize = 20;

  // Data
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [benchmarks, setBenchmarks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const hasSearched = searchQuery.trim().length > 0;

  // Sync state → URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (quality) params.set('quality', quality);
    if (sortBy !== 'best_price_cad:asc') params.set('sort', sortBy);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [searchQuery, quality, sortBy, page, setSearchParams]);

  // Fetch global stats on mount
  const fetchStats = useCallback(async () => {
    try {
      const data = await referencePricingAPI.getStats();
      setStats(data);
    } catch (err) {
      console.error('Error fetching pricing stats:', err);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Fetch products when search/filter changes
  const fetchProducts = useCallback(async () => {
    if (!hasSearched) {
      setProducts([]);
      setTotal(0);
      setBenchmarks(null);
      return;
    }

    setLoading(true);
    try {
      const [field, order] = sortBy.split(':');
      const params = {
        page,
        size: pageSize,
        sort_by: field,
        sort_order: order || 'asc',
        q: searchQuery,
      };
      if (quality) params.data_quality = quality;

      const [productData, benchmarkData] = await Promise.all([
        referencePricingAPI.list(params),
        referencePricingAPI.getBenchmarks('', searchQuery).catch(() => ({ benchmarks: [] })),
      ]);

      setProducts(productData.products || []);
      setTotal(productData.total || 0);
      setBenchmarks(benchmarkData.benchmarks || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, quality, sortBy, page, hasSearched]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Search handlers
  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    setSearchQuery(trimmed);
    setPage(1);
  };

  const handlePopularSearch = (term) => {
    setSearchInput(term);
    setSearchQuery(term);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPage(1);
    setBenchmarks(null);
  };

  const totalPages = Math.ceil(total / pageSize);

  // Initial loading state
  if (initialLoading) {
    return (
      <div className="analytics">
        <div className="analytics-loading">
          <RefreshCw size={32} className="spinning" />
          <p>Loading Product Intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics">
      {/* Header */}
      <div className="pi-header">
        <h1>Product Intelligence</h1>
        <p className="pi-subtitle">Search products, compare pricing across sources, and explore market intelligence</p>
      </div>

      {/* Search Bar — mirrors Dashboard */}
      <div className="pi-search-controls">
        <form className="pi-search-bar" onSubmit={handleSearch}>
          <Search className="pi-search-icon" />
          <input
            type="search"
            placeholder="Search products, categories, or supplies..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>
      </div>

      {/* Popular Searches */}
      {!hasSearched && (
        <div className="pi-popular">
          <span className="pi-popular-label">Popular:</span>
          {POPULAR_SEARCHES.map(term => (
            <button
              key={term}
              className="pi-popular-tag"
              onClick={() => handlePopularSearch(term)}
            >
              {term}
            </button>
          ))}
        </div>
      )}

      {/* === LANDING STATE (no search) === */}
      {!hasSearched && stats && (
        <div className="pi-landing">
          <div className="pi-landing-stats">
            <div className="pi-stat-card">
              <div className="pi-stat-icon icon-primary"><Package size={24} /></div>
              <div className="pi-stat-content">
                <span className="pi-stat-value">{(stats.total_products || 0).toLocaleString()}</span>
                <span className="pi-stat-label">Products Indexed</span>
              </div>
            </div>
            <div className="pi-stat-card">
              <div className="pi-stat-icon icon-info"><BarChart3 size={24} /></div>
              <div className="pi-stat-content">
                <span className="pi-stat-value">{stats.avg_sources_per_product || 0}</span>
                <span className="pi-stat-label">Avg Sources / Product</span>
              </div>
            </div>
            <div className="pi-stat-card">
              <div className="pi-stat-icon icon-success"><TrendingUp size={24} /></div>
              <div className="pi-stat-content">
                <span className="pi-stat-value">{(stats.quality_breakdown?.high || 0).toLocaleString()}</span>
                <span className="pi-stat-label">High Quality (3+ sources)</span>
              </div>
            </div>
            <div className="pi-stat-card">
              <div className="pi-stat-icon icon-warning"><DollarSign size={24} /></div>
              <div className="pi-stat-content">
                <span className="pi-stat-value">{(stats.total_price_points || 0).toLocaleString()}</span>
                <span className="pi-stat-label">Total Price Points</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === SEARCH RESULTS STATE === */}
      {hasSearched && (
        <>
          {/* Category Summary */}
          {!loading && products.length > 0 && (
            <CategorySummary
              products={products}
              benchmarks={benchmarks}
              searchQuery={searchQuery}
            />
          )}

          {/* Filter Bar */}
          <div className="pi-filter-bar">
            <select
              value={quality}
              onChange={(e) => { setQuality(e.target.value); setPage(1); }}
            >
              <option value="">All Quality</option>
              <option value="high">High (3+)</option>
              <option value="medium">Medium (2)</option>
              <option value="low">Low (1)</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            >
              <option value="best_price_cad:asc">Price: Low to High</option>
              <option value="best_price_cad:desc">Price: High to Low</option>
              <option value="price_count:desc">Most Sources</option>
              <option value="product_name.keyword:asc">Name A-Z</option>
              <option value="last_updated:desc">Recently Updated</option>
            </select>
            <span className="pi-filter-count">
              {loading ? 'Searching...' : `Showing ${total.toLocaleString()} results`}
            </span>
          </div>

          {/* Loading */}
          {loading && (
            <div className="pi-search-loading">
              <RefreshCw size={24} className="spinning" />
              <p>Searching products...</p>
            </div>
          )}

          {/* Product Cards */}
          {!loading && products.length === 0 && (
            <div className="pi-no-results">
              <Search size={48} />
              <h3>No products found</h3>
              <p>Try a different search term or adjust your filters.</p>
            </div>
          )}

          {!loading && products.length > 0 && (
            <div className="pi-product-list">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="pi-pagination">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </button>
              <span className="pi-pagination-info">
                Page {page} of {totalPages} ({total.toLocaleString()} products)
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Analytics;
